import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

interface PingDBSchema extends DBSchema {
  metadata: {
    key: string;
    value: {
      key: string;
      value: string;
      updatedAt: number;
    };
  };
  /**
   * Per-conversation opaque WS-resume cursor (the `next_cursor` returned by
   * `GET /v1/messages?conv=&cursor=`). Read on socket connect to build a
   * `{type:'resume', cursors:[...]}` frame so a tab refresh re-joins exactly
   * where the previous tab left off without paying for a full REST catch-up.
   * Key is the conversation id in hex (lower-case).
   */
  sync_cursors: {
    key: string;
    value: {
      conversationIdHex: string;
      cursor: string;
      updatedAt: number;
    };
  };
}

let dbInstance: IDBPDatabase<PingDBSchema> | null = null;

export async function getDatabase(): Promise<IDBPDatabase<PingDBSchema>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<PingDBSchema>('ping-db', 2, {
    upgrade(db, oldVersion) {
      if (!db.objectStoreNames.contains('metadata')) {
        db.createObjectStore('metadata', { keyPath: 'key' });
      }
      if (oldVersion < 2 && !db.objectStoreNames.contains('sync_cursors')) {
        db.createObjectStore('sync_cursors', { keyPath: 'conversationIdHex' });
      }
    },
  });

  return dbInstance;
}

export async function closeDatabase(): Promise<void> {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}

export async function putMetadata(key: string, value: string): Promise<void> {
  const db = await getDatabase();
  await db.put('metadata', {
    key,
    value,
    updatedAt: Date.now(),
  });
}

export async function getMetadata(key: string): Promise<string | undefined> {
  const db = await getDatabase();
  const record = await db.get('metadata', key);
  return record?.value;
}

export async function deleteMetadata(key: string): Promise<void> {
  const db = await getDatabase();
  await db.delete('metadata', key);
}

export async function clearAllData(): Promise<void> {
  const db = await getDatabase();
  await db.clear('metadata');
  await db.clear('sync_cursors');
}

// --- sync cursors ---------------------------------------------------------

export async function putSyncCursor(
  conversationIdHex: string,
  cursor: string,
): Promise<void> {
  const db = await getDatabase();
  await db.put('sync_cursors', {
    conversationIdHex: conversationIdHex.toLowerCase(),
    cursor,
    updatedAt: Date.now(),
  });
}

export async function getSyncCursor(conversationIdHex: string): Promise<string | null> {
  const db = await getDatabase();
  const row = await db.get('sync_cursors', conversationIdHex.toLowerCase());
  return row?.cursor ?? null;
}

export async function deleteSyncCursor(conversationIdHex: string): Promise<void> {
  const db = await getDatabase();
  await db.delete('sync_cursors', conversationIdHex.toLowerCase());
}

export async function snapshotSyncCursors(): Promise<
  ReadonlyArray<{ conversationIdHex: string; cursor: string }>
> {
  const db = await getDatabase();
  const rows = await db.getAll('sync_cursors');
  return rows.map((r) => ({ conversationIdHex: r.conversationIdHex, cursor: r.cursor }));
}
